import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { getAPIBaseURL } from '../../utils/environment';

const BuildInfoModal = ({ open, setOpen }) => {
  const [copiedField, setCopiedField] = useState(null);
  const [apiBaseUrl, setApiBaseUrl] = useState('Loading...');

  // These are defined in vite.config.js at build time
  const commitSha =
    typeof __VITE_VERCEL_COMMIT_SHA__ !== 'undefined' ? __VITE_VERCEL_COMMIT_SHA__ : 'dev';
  const buildTime = typeof __VITE_BUILD_TIME__ !== 'undefined' ? __VITE_BUILD_TIME__ : null;
  const environment = typeof __VITE_ENVIRONMENT__ !== 'undefined' ? __VITE_ENVIRONMENT__ : 'local';
  const commitRef =
    typeof __VITE_VERCEL_COMMIT_REF__ !== 'undefined' ? __VITE_VERCEL_COMMIT_REF__ : null;

  // Fetch the API base URL
  useEffect(() => {
    const fetchApiUrl = async () => {
      const url = await getAPIBaseURL();
      setApiBaseUrl(url || 'Not configured');
    };
    fetchApiUrl();
  }, []);

  // Format build time for display
  const formattedBuildTime = buildTime
    ? new Date(buildTime).toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      })
    : 'Not available';

  // Create GitHub link if we have a commit SHA
  const githubLink =
    commitSha && commitSha !== 'dev'
      ? `https://github.com/your-org/your-repo/commit/${commitSha}`
      : null;

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const InfoRow = ({ label, value, copyable = false, link = null }) => (
    <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
      <dt className='text-sm font-medium text-gray-500'>{label}</dt>
      <dd className='mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 flex items-center justify-between'>
        {link ? (
          <a
            href={link}
            target='_blank'
            rel='noopener noreferrer'
            className='text-emerald-600 hover:text-emerald-700 hover:underline font-mono break-all'>
            {value}
          </a>
        ) : (
          <span className='font-mono break-all'>{value}</span>
        )}
        {copyable && (
          <button
            onClick={() => copyToClipboard(value, label)}
            className='ml-2 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0'
            title='Copy to clipboard'>
            {copiedField === label ? (
              <CheckCircleIcon className='h-5 w-5 text-green-500' />
            ) : (
              <ClipboardDocumentIcon className='h-5 w-5' />
            )}
          </button>
        )}
      </dd>
    </div>
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as='div' className='relative z-[70]' onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'>
              <Dialog.Panel className='relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6'>
                <div className='absolute right-0 top-0 pr-4 pt-4'>
                  <button
                    type='button'
                    className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none'
                    onClick={() => setOpen(false)}>
                    <span className='sr-only'>Close</span>
                    <XMarkIcon className='h-6 w-6' aria-hidden='true' />
                  </button>
                </div>

                <div className='sm:flex sm:items-start'>
                  <div className='mt-3 text-center sm:mt-0 sm:text-left w-full'>
                    <Dialog.Title
                      as='h3'
                      className='text-lg font-semibold leading-6 text-gray-900 mb-4'>
                      Build Information
                    </Dialog.Title>

                    <div className='border border-gray-200 rounded-lg overflow-hidden'>
                      <dl className='divide-y divide-gray-200'>
                        <InfoRow label='Backend API' value={apiBaseUrl} copyable={true} />

                        <InfoRow
                          label='Commit SHA'
                          value={commitSha}
                          copyable={true}
                          link={githubLink}
                        />

                        {commitRef && <InfoRow label='Branch' value={commitRef} copyable={true} />}

                        <InfoRow label='Environment' value={environment} copyable={false} />

                        <InfoRow label='Build Time' value={formattedBuildTime} copyable={false} />
                      </dl>
                    </div>

                    <div className='mt-4 text-xs text-gray-500'>
                      <p>
                        This information can be used to verify the deployed version in the Vercel
                        dashboard.
                      </p>
                      <p className='mt-1'>
                        Go to Deployments → find the matching commit SHA to confirm the current
                        deployment.
                      </p>
                    </div>
                  </div>
                </div>

                <div className='mt-5 sm:mt-4 sm:flex sm:flex-row-reverse'>
                  <button
                    type='button'
                    className='mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto'
                    onClick={() => setOpen(false)}>
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default BuildInfoModal;
